import React from "react";
import { cn } from "../../../lib/utils";
import { Radio } from "../../ui/radio";
import QuestionFrame from "./QuestionFrame";
import OtherSpecifyField from "./OtherSpecifyField";
import {
  OTHER_OPTION_ID,
  parseSingleChoice,
  serializeSingleChoice,
} from "../surveyUtils";

/**
 * Single-choice question, with optional Other + specify field.
 *
 * @param {Object} props
 * @param {object} props.question
 * @param {unknown} [props.value]
 * @param {function(unknown, {{ immediate?: boolean }}): void} props.onChange
 * @param {string} [props.error]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.showRequired]
 * @returns {JSX.Element}
 */
export default function SingleChoiceQuestion({
  question,
  value,
  onChange,
  error,
  readOnly,
  showRequired,
}) {
  const name = `survey-q-${question.id}`;
  const options = question.options || [];
  const allowOther = Boolean(question.allowOther);
  const { optionId, otherText } = parseSingleChoice(value);
  const otherSelected = optionId === OTHER_OPTION_ID;

  /**
   * @param {string} nextId
   * @returns {void}
   */
  function selectOption(nextId) {
    onChange(serializeSingleChoice(nextId, nextId === OTHER_OPTION_ID ? otherText : ""), {
      immediate: true,
    });
  }

  return (
    <QuestionFrame question={question} indent showRequired={showRequired}>
      <fieldset className="m-0 space-y-1 border-0 p-0" disabled={readOnly}>
        <legend className="sr-only">{question.prompt}</legend>
        {options.map((option) => (
          <label
            key={option.id}
            className={cn(
              "flex items-center gap-3 rounded-md py-2 transition-colors",
              readOnly ? "cursor-default" : "cursor-pointer hover:bg-ui-muted/50"
            )}
          >
            <Radio
              name={name}
              value={option.id}
              checked={optionId === option.id}
              disabled={readOnly}
              onSelect={() => selectOption(option.id)}
              aria-label={option.label}
            />
            <span className="text-sm text-text-primary">{option.label}</span>
          </label>
        ))}
        {allowOther ? (
          <div>
            <label
              className={cn(
                "flex items-center gap-3 rounded-md py-2 transition-colors",
                readOnly ? "cursor-default" : "cursor-pointer hover:bg-ui-muted/50"
              )}
            >
              <Radio
                name={name}
                value={OTHER_OPTION_ID}
                checked={otherSelected}
                disabled={readOnly}
                onSelect={() => selectOption(OTHER_OPTION_ID)}
                aria-label="Other"
              />
              <span className="text-sm text-text-primary">Other</span>
            </label>
            {otherSelected ? (
              <OtherSpecifyField
                id={`${name}-other`}
                value={otherText}
                readOnly={readOnly}
                error={error}
                onChange={(text) =>
                  onChange(serializeSingleChoice(OTHER_OPTION_ID, text), { immediate: false })
                }
              />
            ) : null}
          </div>
        ) : null}
      </fieldset>
    </QuestionFrame>
  );
}
